import React, { useMemo } from "react";
import { DollarsOnlyFormatter } from "@upserve/financials";
import { pathOr } from "ramda";
import { TooltipProps } from "recharts";
import { Maybe } from "true-myth";
import styled from "styles/styled_components";
import title from "styles/title";

import LaborTitle from "components/labor/labor_title";
import Tooltip, { TOOLTIP_WIDTHS } from "components/shared/tooltip";
import { getLastYear } from "helpers/dates";
import { LaborCostsPerDay } from "helpers/insights/labor_costs";
import {
  getDollarsOrEmpty,
  getPercentOrEmpty,
  getSalesForecastForDay,
} from "helpers/insights/sales_forecast";
import { ORDERED_CATEGORIES, categoryColors } from "helpers/schedule/roles";
import { ISalesForecast } from "types/account";
import { percentage } from "helpers/math";

interface IStyledValueSection {
  border?: string;
}

const StyledGridWrapper = styled.div`
  display: grid;
  grid-gap: ${props => props.theme.spacing.xsmall};
  grid-template-columns: repeat(
    auto-fit,
    minmax(${props => props.theme.spacing.small}, 1fr)
  );
  grid-template-rows: 1fr;
`;

const StyledValueSection = styled("section")<IStyledValueSection>`
  line-height: ${props => props.theme.text.lineHeight.medium};
  margin-top: ${props => props.theme.spacing.xsmall};

  &:not(:last-child) {
    padding-bottom: ${props => props.theme.spacing.xsmall};
  }

  & header {
    font-size: ${props => props.theme.text.size.base};
    margin-top: ${props => props.theme.spacing.xsmall};
    ${title}
  }
  & div {
    margin-top: ${props => props.theme.spacing.xsmall};
  }
`;

const StyledValue = styled.div`
  font-size: ${props => props.theme.text.size.largest};
  &.medium {
    font-family: ${props => props.theme.text.mediumFamily};
  }
`;

const StyledCategoryValue = styled("div")<IStyledValueSection>`
  border-left: 10px solid ${props => props.border};
  font-size: ${props => props.theme.text.size.base};
  line-height: ${props => props.theme.text.lineHeight.small};
  margin-bottom: ${props => props.theme.spacing.xsmall};
  padding-left: ${props => props.theme.spacing.xsmall};
`;

interface ILaborCostTooltip extends TooltipProps {
  costsPerDay: LaborCostsPerDay;
  pointer: "left" | "right";
  salesForecast: Maybe<ISalesForecast>;
}

function LaborCostTooltip({
  costsPerDay,
  label,
  payload,
  pointer,
  salesForecast,
}: ILaborCostTooltip) {
  const dateString: string = pathOr("", [0, "payload", "dateString"], payload);
  const dayIndex: number = pathOr(-1, [0, "payload", "dayIndex"], payload);
  const costsToday = costsPerDay.get(dateString)!;
  const isAgainstForecast = salesForecast.isJust();
  const lastYear = getLastYear();

  const valueGroups: JSX.Element[][] = useMemo(
    () => {
      if (!dateString || dayIndex === -1) {
        return [];
      }
      const totalCost = costsToday.Total;
      const totalCostSect = (
        <StyledValueSection key="cost">
          <header>Scheduled Labor Cost</header>
          <StyledValue>{DollarsOnlyFormatter.format(totalCost)}</StyledValue>
        </StyledValueSection>
      );

      const salesValue = getSalesForecastForDay(salesForecast, dayIndex);

      const salesSect = (
        <StyledValueSection key="gross">
          <header>{lastYear} Gross Sales</header>
          <StyledValue>{getDollarsOrEmpty(salesValue)}</StyledValue>
        </StyledValueSection>
      );

      // Calculate category percentages one of three ways:
      // (A) Forecast does not exist => percentage of totalCost
      // (B) Forecast exists but today's value ==0 => straight dollar amounts (no %)
      // (C) Forecast exists and today's value !=0 => percentage of forecast
      const isDisplayingPercent = !!salesValue || !isAgainstForecast;
      const categoryValues = ORDERED_CATEGORIES.map(cat => {
        const catCost = costsToday[cat];
        const denominator = isAgainstForecast ? salesValue : totalCost;
        let catDisplayValue;
        if (isDisplayingPercent) {
          catDisplayValue = percentage(catCost, denominator);
        } else {
          catDisplayValue = catCost
            ? DollarsOnlyFormatter.format(catCost)
            : "$0";
        }

        return (
          <StyledCategoryValue
            border={categoryColors[cat]}
            key={`category-${cat}`}>
            {catDisplayValue} {cat}
          </StyledCategoryValue>
        );
      });
      const categorySect = (
        <StyledValueSection key="categories">
          <header>
            Labor Cost{" "}
            {isAgainstForecast
              ? `(${isDisplayingPercent ? "%" : "$"})`
              : "By Category"}
          </header>
          {categoryValues}
        </StyledValueSection>
      );

      if (!isAgainstForecast) {
        // Case (A)
        return [[totalCostSect, categorySect]];
      }

      if (!isDisplayingPercent) {
        // Case (B)
        return [[totalCostSect, salesSect], [categorySect]];
      }

      const percentSect = (
        <StyledValueSection key="percent">
          <StyledValue className="medium">
            {getPercentOrEmpty(totalCost, salesValue)} Labor Cost
          </StyledValue>
        </StyledValueSection>
      );

      // Case (C)
      return [[totalCostSect, salesSect], [categorySect, percentSect]];
    },
    [costsToday, dayIndex, salesForecast]
  );

  const columns = valueGroups.map((valGroup, idx) => (
    <div className="gridCol" key={idx}>
      {valGroup}
    </div>
  ));

  return (
    <Tooltip
      pointer={pointer}
      pointerBottom
      width={isAgainstForecast ? TOOLTIP_WIDTHS.large : TOOLTIP_WIDTHS.medium}>
      <LaborTitle>{label}</LaborTitle>
      <StyledGridWrapper>{columns}</StyledGridWrapper>
    </Tooltip>
  );
}

export default LaborCostTooltip;
